import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Loader,
} from "@devhop/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  describeViewerError,
  publicFormsViewerApi,
} from "@/modules/forms/presentation/public/public-forms-viewer-api";
import { config } from "@/shared/lib/config";

export function PublicFormViewerGatePage() {
  const { formId } = useParams({ from: "/f/$formId/view" });
  const resolvedId =
    typeof formId === "string" && formId.length > 0 ? formId : "";
  const navigate = useNavigate();
  const [pin, setPin] = useState("");

  const meta = useQuery({
    queryKey: ["public-form-viewer-meta", resolvedId],
    queryFn: () => publicFormsViewerApi.getMeta(resolvedId),
    enabled: resolvedId.length > 0,
    retry: false,
  });

  useEffect(() => {
    if (!meta.data?.sessionValid || !resolvedId) return;
    void navigate({
      to: "/f/$formId/view/responses",
      params: { formId: resolvedId },
    });
  }, [meta.data?.sessionValid, navigate, resolvedId]);

  const verifyMut = useMutation({
    mutationFn: () => publicFormsViewerApi.verify(resolvedId, pin.trim()),
    onSuccess: async () => {
      await navigate({
        to: "/f/$formId/view/responses",
        params: { formId: resolvedId },
      });
    },
  });

  if (!resolvedId) {
    return <LoadErrorPane message="ລິ້ງບໍ່ຖືກຕ້ອງ ຫຼື ບໍ່ພົບຟອມ" />;
  }

  if (meta.isLoading) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-muted-foreground">
        <Loader />
        <span className="text-sm">ກຳລັງໂຫຼດ…</span>
      </div>
    );
  }

  if (meta.isError) {
    const code = (meta.error as Error & { code?: string }).code ?? "UNKNOWN";
    return <LoadErrorPane message={describeViewerError(code)} />;
  }

  const m = meta.data;
  if (!m) return null;

  if (!m.viewerPinConfigured) {
    return <LoadErrorPane message="ຍັງບໍ່ເປີດໃຫ້ເບິ່ງຄຳຕອບຜ່ານລິ້ງນີ້ — ຕິດຕໍ່ຜູ້ຈັດຟອມ" />;
  }

  if (m.viewerLinkExpired) {
    return <LoadErrorPane message={describeViewerError("VIEWER_LINK_EXPIRED")} />;
  }

  if (!m.hasResponses || !m.canView) {
    return <LoadErrorPane message="ຍັງບໍ່ມີຄຳຕອບໃນຟອມນີ້" />;
  }

  return (
    <main className="mx-auto flex w-full max-w-md flex-col gap-6 px-4 py-10">
      <header className="space-y-1 text-center">
        <h1 className="font-semibold text-xl tracking-tight">{m.title}</h1>
        <p className="text-muted-foreground text-sm">
          ເບິ່ງຄຳຕອບ — {config.brandName}
        </p>
      </header>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">ໃສ່ PIN</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="viewer-pin">ລະຫັດ PIN 6 ຫຼັກ</Label>
            <Input
              id="viewer-pin"
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={pin}
              onChange={(e) =>
                setPin(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="••••••"
            />
          </div>
          {verifyMut.isError ? (
            <p className="text-destructive text-sm" role="alert">
              {describeViewerError(
                (verifyMut.error as Error & { code?: string }).code ??
                  "VIEWER_ACCESS_DENIED",
              )}
            </p>
          ) : null}
          <Button
            type="button"
            className="w-full"
            disabled={pin.length !== 6 || verifyMut.isPending}
            onClick={() => verifyMut.mutate()}
          >
            {verifyMut.isPending ? "ກຳລັງກວດ…" : "ເຂົ້າເບິ່ງຄຳຕອບ"}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}

function LoadErrorPane({ message }: { message: string }) {
  return (
    <div className="flex min-h-[50vh] items-center justify-center p-6">
      <Card className="w-full max-w-lg border-destructive/40">
        <CardContent className="space-y-2 pt-6">
          <h1 className="font-semibold text-lg">{message}</h1>
        </CardContent>
      </Card>
    </div>
  );
}
